import { StateField } from '@codemirror/state';
import type { Extension, Range, Text } from '@codemirror/state';
import { Decoration, EditorView } from '@codemirror/view';
import type { DecorationSet } from '@codemirror/view';
import { ANSI_COLOR_NAMES, parseAnsiDocument } from './ansi-escape-parser';
import type { AnsiColorName, AnsiStyle } from './ansi-escape-parser';

type AnsiPalette = Record<AnsiColorName, string>;

export type AnsiThemeMode = 'light' | 'dark';

const ANSI_PALETTE_LIGHT: AnsiPalette = {
  black: '#24292f',
  red: '#cf222e',
  green: '#116329',
  yellow: '#9a6700',
  blue: '#0969da',
  magenta: '#8250df',
  cyan: '#1b7c83',
  white: '#6e7781',
  brightBlack: '#57606a',
  brightRed: '#a40e26',
  brightGreen: '#1a7f37',
  brightYellow: '#7d4e00',
  brightBlue: '#218bff',
  brightMagenta: '#a475f9',
  brightCyan: '#3192aa',
  brightWhite: '#8c959f',
};

const ANSI_PALETTE_DARK: AnsiPalette = {
  black: '#6e7681',
  red: '#ff7b72',
  green: '#3fb950',
  yellow: '#d29922',
  blue: '#58a6ff',
  magenta: '#bc8cff',
  cyan: '#39c5cf',
  white: '#b1bac4',
  brightBlack: '#8b949e',
  brightRed: '#ffa198',
  brightGreen: '#56d364',
  brightYellow: '#e3b341',
  brightBlue: '#79c0ff',
  brightMagenta: '#d2a8ff',
  brightCyan: '#56d4dd',
  brightWhite: '#f0f6fc',
};

const ANSI_CLASS_PREFIX = 'cm-ansi';
const ANSI_BOLD_CLASS = `${ANSI_CLASS_PREFIX}-bold`;
const ANSI_DIM_CLASS = `${ANSI_CLASS_PREFIX}-dim`;
const ANSI_ITALIC_CLASS = `${ANSI_CLASS_PREFIX}-italic`;
const ANSI_UNDERLINE_CLASS = `${ANSI_CLASS_PREFIX}-underline`;

const foregroundClassName = (color: AnsiColorName): string =>
  `${ANSI_CLASS_PREFIX}-${color}`;

const createAnsiTheme = (palette: AnsiPalette): Extension =>
  EditorView.theme({
    ...Object.fromEntries(
      ANSI_COLOR_NAMES.map((color) => [
        `.${foregroundClassName(color)}`,
        { color: palette[color] },
      ]),
    ),
    [`.${ANSI_BOLD_CLASS}`]: { fontWeight: '600' },
    [`.${ANSI_DIM_CLASS}`]: { opacity: '0.7' },
    [`.${ANSI_ITALIC_CLASS}`]: { fontStyle: 'italic' },
    [`.${ANSI_UNDERLINE_CLASS}`]: { textDecoration: 'underline' },
  });

const ANSI_THEMES: Record<AnsiThemeMode, Extension> = {
  light: createAnsiTheme(ANSI_PALETTE_LIGHT),
  dark: createAnsiTheme(ANSI_PALETTE_DARK),
};

const HIDDEN_ESCAPE_DECORATION = Decoration.replace({});

const toAnsiClassName = (style: AnsiStyle): string =>
  [
    style.foreground ? foregroundClassName(style.foreground) : null,
    style.bold ? ANSI_BOLD_CLASS : null,
    style.dim ? ANSI_DIM_CLASS : null,
    style.italic ? ANSI_ITALIC_CLASS : null,
    style.underline ? ANSI_UNDERLINE_CLASS : null,
  ]
    .filter((className): className is string => className !== null)
    .join(' ');

const buildAnsiDecorations = (doc: Text): DecorationSet => {
  const { escapeRanges, styledRanges } = parseAnsiDocument(doc.toString());
  const marksByClassName = new Map<string, Decoration>();
  const markFor = (className: string): Decoration => {
    const cached = marksByClassName.get(className);

    if (cached) {
      return cached;
    }

    const mark = Decoration.mark({ class: className });

    marksByClassName.set(className, mark);

    return mark;
  };

  const ranges: Range<Decoration>[] = [
    ...escapeRanges.map(({ from, to }) =>
      HIDDEN_ESCAPE_DECORATION.range(from, to),
    ),
    ...styledRanges.map(({ from, to, style }) =>
      markFor(toAnsiClassName(style)).range(from, to),
    ),
  ];

  return Decoration.set(ranges, true);
};

const ansiDecorationsField = StateField.define<DecorationSet>({
  create: (state) => buildAnsiDecorations(state.doc),
  update: (decorations, transaction) =>
    transaction.docChanged
      ? buildAnsiDecorations(transaction.state.doc)
      : decorations,
  provide: (field) => EditorView.decorations.from(field),
});

export const ansiEscapeHighlighting = (mode: AnsiThemeMode): Extension => [
  ansiDecorationsField,
  ANSI_THEMES[mode],
];
